import { FC, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { BaseDrawer, FormDrawer } from "@ryde/layout";
import {
  useModalConfirm,
  usePageContext,
  useRidesBulkActions,
  useSelectedIds,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  bulkJourneyAssignFormDrawerAF,
  ConfirmResult,
  IRideDto,
  isRideEntity,
  RideCrudModeKey,
  selectedTableRowsValues,
} from "@ryde/shared";

import {
  BulkJourneyAssignForm,
  bulkJourneyAssignFormValidationRules,
} from "./parts";

interface IBulkJourneyAssignForm {
  journeyId: number;
}

export const BulkJourneyAssignFormRidesDrawer: FC = () => {
  const { t } = useTranslation();
  const { entity: currentPageEntity } = usePageContext();
  const rideEntity = RideCrudModeKey.uiUnit;
  const { confirmDialogResult } = useModalConfirm();
  const { selectedRowsIds: selectedRideIds } = useSelectedIds();

  const setBulkJourneyAssignFormDrawerOpened = useSetRecoilState(
    bulkJourneyAssignFormDrawerAF(rideEntity),
  );

  const rideRows = useRecoilValue(
    selectedTableRowsValues(currentPageEntity),
  ) as IRideDto[] | undefined;

  const isOneRideSelected = selectedRideIds.length === 1;

  const resolver = useYupValidationResolver(
    bulkJourneyAssignFormValidationRules,
    rideEntity,
  );

  const methods = useForm<IBulkJourneyAssignForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    resolver,
    defaultValues: {
      journeyId: undefined,
    },
  });
  const {
    getValues,
    trigger,
    setValue,
    formState: { isValid, errors },
  } = methods;
  const isSaveDisabled = Boolean(!isValid || Object.keys(errors || {}).length);

  const { bulkRidesJourneyAssign } = useRidesBulkActions();

  const onSaveClick = async (): Promise<void> => {
    await trigger();

    if (!isValid) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t(`general.PROCEED_QUESTION`),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const { journeyId } = getValues();

    const response = await bulkRidesJourneyAssign({
      ids: selectedRideIds,
      journeyId: journeyId ?? null,
    });

    if (response.success) {
      setBulkJourneyAssignFormDrawerOpened(false);
    }
  };

  const onCloseClick = (): void => {
    setBulkJourneyAssignFormDrawerOpened(false);
  };

  useEffect(() => {
    if (!isOneRideSelected || !isRideEntity(currentPageEntity)) {
      return;
    }

    const selectedRideId = selectedRideIds.at(0);

    if (!selectedRideId) {
      return;
    }

    const rideDto = rideRows?.find(({ id }) => selectedRideId === id);

    if (!rideDto?.journeyId) {
      return;
    }

    setValue("journeyId", rideDto.journeyId, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [
    rideRows,
    currentPageEntity,
    isOneRideSelected,
    selectedRideIds,
    setValue,
  ]);

  return (
    <BaseDrawer onCloseDrawer={onCloseClick}>
      <FormDrawer
        onCloseDrawer={onCloseClick}
        onCancelClick={onCloseClick}
        title={t("ride.assignAJourney")}
        onSaveClick={onSaveClick}
        footerSaveLabel={t("general.SAVE")}
        isSaveDisabled={isSaveDisabled}
      >
        <FormProvider {...methods}>
          <BulkJourneyAssignForm />
        </FormProvider>
      </FormDrawer>
    </BaseDrawer>
  );
};
